import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Button } from './ui/button';

const MERCHANT_ID = process.env.REACT_APP_MERCHANT_ID;
const COOKIE_CONSENT_KEY = `storefront_cookie_consent_${MERCHANT_ID}`;

export default function CookieConsent() {
    const [showBanner, setShowBanner] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [preferences, setPreferences] = useState({
        necessary: true,
        analytics: false,
        marketing: false
    });

    useEffect(() => {
        const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (!consent) {
            setTimeout(() => setShowBanner(true), 1000);
        } else {
            try {
                setPreferences(JSON.parse(consent));
            } catch (e) {
                setShowBanner(true);
            }
        }
    }, []);

    const handleAcceptAll = () => {
        const allAccepted = {
            necessary: true,
            analytics: true,
            marketing: true,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(allAccepted));
        setPreferences(allAccepted);
        setShowBanner(false);
    };

    const handleAcceptNecessary = () => {
        const necessaryOnly = {
            necessary: true,
            analytics: false,
            marketing: false,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(necessaryOnly));
        setPreferences(necessaryOnly);
        setShowBanner(false);
    };

    const handleSavePreferences = () => {
        const prefs = { ...preferences, timestamp: new Date().toISOString() };
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
        setShowBanner(false);
        setShowSettings(false);
    };

    const togglePreference = (key) => {
        if (key === 'necessary') return;
        setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
    };

    if (!showBanner) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                className="fixed bottom-0 left-0 right-0 z-[100] p-3 sm:p-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            >
                <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl border overflow-hidden">
                    {!showSettings ? (
                        <div className="p-5">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                                    <Cookie className="w-5 h-5 text-amber-600" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900 mb-1">
                                        Cookies
                                    </h3>
                                    <p className="text-gray-600 text-sm mb-4">
                                        Χρησιμοποιούμε cookies για καλύτερη εμπειρία.{' '}
                                        <a href="/privacy" className="text-primary hover:underline">
                                            Μάθετε περισσότερα
                                        </a>
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        <Button onClick={handleAcceptAll} size="sm">
                                            <Check className="w-3 h-3 mr-1" />
                                            Αποδοχή
                                        </Button>
                                        <Button onClick={handleAcceptNecessary} variant="outline" size="sm">
                                            Μόνο Απαραίτητα
                                        </Button>
                                        <Button onClick={() => setShowSettings(true)} variant="ghost" size="sm">
                                            <Settings className="w-3 h-3 mr-1" />
                                            Ρυθμίσεις
                                        </Button>
                                    </div>
                                </div>
                                <button onClick={handleAcceptNecessary} className="text-gray-400 hover:text-gray-600">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold text-gray-900">Ρυθμίσεις Cookies</h3>
                                <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-3 mb-4">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div>
                                        <h4 className="font-medium text-sm">Απαραίτητα</h4>
                                        <p className="text-xs text-gray-500">Για τη λειτουργία του site</p>
                                    </div>
                                    <div className="w-10 h-5 bg-green-500 rounded-full flex items-center justify-end px-0.5">
                                        <div className="w-4 h-4 bg-white rounded-full"></div>
                                    </div>
                                </div>

                                <div 
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer"
                                    onClick={() => togglePreference('analytics')}
                                >
                                    <div>
                                        <h4 className="font-medium text-sm">Analytics</h4>
                                        <p className="text-xs text-gray-500">Στατιστικά επισκεψιμότητας</p>
                                    </div>
                                    <div className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${
                                        preferences.analytics ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'
                                    }`}>
                                        <div className="w-4 h-4 bg-white rounded-full"></div>
                                    </div>
                                </div>

                                <div 
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer"
                                    onClick={() => togglePreference('marketing')}
                                >
                                    <div>
                                        <h4 className="font-medium text-sm">Marketing</h4>
                                        <p className="text-xs text-gray-500">Εξατομικευμένες προσφορές</p>
                                    </div>
                                    <div className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${
                                        preferences.marketing ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'
                                    }`}>
                                        <div className="w-4 h-4 bg-white rounded-full"></div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <Button onClick={handleSavePreferences} size="sm">Αποθήκευση</Button>
                                <Button onClick={handleAcceptAll} variant="outline" size="sm">Αποδοχή Όλων</Button>
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
